import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../../services/api';
import { TrendingUp, Banknote, Clock, Users, Truck, ArrowRight } from 'lucide-react';
import { applyTenantBranding } from '../../utils/portal';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import NewsCard from '../../components/NewsCard';
import PayoutOverview from '../../components/PayoutOverview';
import { formatCurrency, formatDate, formatDeduction, formatLongDate } from '../../utils/format';

export default function VendorDashboard() {
  const navigate = useNavigate();

  // The server decides whether the session is valid (idle timeout, max age, logout, blocked login);
  // any 401 sends the user to the login page (services/api.ts)

  const { data: vendorData, isLoading, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const response = await vendorApi.me();
      return response.data;
    },
  });

  const { data: dashboardData } = useQuery({
    queryKey: ['vendor', 'dashboard-data'],
    queryFn: async () => {
      const response = await vendorApi.getDashboardData();
      return response.data;
    },
  });

  const { data: invoicesData } = useQuery({
    queryKey: ['vendor', 'invoices'],
    queryFn: async () => {
      const response = await vendorApi.getInvoices();
      return response.data;
    },
  });

  // Next settlement estimate, open invoices, last payment (needs Settled.Portal 1.5.0.4; hidden until then)
  const { data: overviewData } = useQuery({
    queryKey: ['vendor', 'overview'],
    queryFn: async () => (await vendorApi.getOverview()).data,
    retry: false,
  });

  // News is short-lived: refetch after a minute so a newly published item shows up quickly
  const { data: newsData, isLoading: newsLoading } = useQuery({
    queryKey: ['vendor', 'news'],
    queryFn: async () => (await vendorApi.getNews()).data,
    staleTime: 60 * 1000,
  });

  const { data: customersData } = useQuery({
    queryKey: ['vendor', 'customers'],
    queryFn: async () => {
      const response = await vendorApi.getCustomers();
      return response.data;
    },
  });

  const { data: resourcesData } = useQuery({
    queryKey: ['vendor', 'resources'],
    queryFn: async () => {
      const response = await vendorApi.getResources();
      return response.data;
    },
  });


  useEffect(() => {
    // Handle any API errors (401 = unauthorized/session expired, 403 = forbidden)
    if (error) {
      const status = (error as any).response?.status;
      if (status === 401 || status === 403) {
        // Session invalid or expired - redirect to login
        console.warn('Session invalid or expired:', error);
        navigate('/login');
      } else {
        // Other errors - log but stay on page for retry
        console.error('API error:', error);
      }
    }
  }, [error, navigate]);

  useEffect(() => {
    if (vendorData?.tenant?.primaryColor) {
      applyTenantBranding(vendorData.tenant.primaryColor);
    }
  }, [vendorData]);

  const handleLogout = async () => {
    try {
      await vendorApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always redirect to login, session is cleared by vendorApi.logout()
      navigate('/login');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Laddar...</p>
        </div>
      </div>
    );
  }

  // Calculate invoice statistics for the chart
  const getInvoiceStats = () => {
    if (!invoicesData?.invoices) return { current: [], lastYear: [], trend: { values: [], centerValue: 0 }, compareEnd: -1 };

    const currentYearData: { [key: string]: number } = {};
    const lastYearData: { [key: string]: number } = {};
    const now = new Date();

    // Initialize last 12 months for current year
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = date.toLocaleDateString('sv-SE', { month: 'short', year: 'numeric' });
      const lastYearDate = new Date(date.getFullYear() - 1, date.getMonth(), 1);
      const lastYearKey = lastYearDate.toLocaleDateString('sv-SE', { month: 'short', year: 'numeric' });
      currentYearData[key] = 0;
      lastYearData[lastYearKey] = 0;
    }

    // Aggregate invoice amounts by month
    invoicesData.invoices.forEach((invoice: any) => {
      const date = new Date(invoice.postingDate);
      const key = date.toLocaleDateString('sv-SE', { month: 'short', year: 'numeric' });
      if (currentYearData[key] !== undefined) {
        currentYearData[key] += invoice.amount || 0;
      } else if (lastYearData[key] !== undefined) {
        lastYearData[key] += invoice.amount || 0;
      }
    });

    const current = Object.entries(currentYearData).map(([month, amount]) => ({ month, amount }));
    const lastYear = Object.entries(lastYearData).map(([month, amount]) => ({ month, amount }));

    // Only months that can be compared: up to the last month with a posted settlement this year, and never
    // the month in progress (index 11). Later months have no figures yet - comparing them with last year
    // would show a slump that isn't there.
    let compareEnd = -1;
    current.forEach((c, i) => {
      if (c.amount !== 0 && i < current.length - 1) compareEnd = i;
    });

    const trend = calculateTrendLine(current, lastYear, compareEnd);

    return { current, lastYear, trend, compareEnd };
  };

  // Difference line (current year - previous year, scaled and centred) for the comparable months only
  const calculateTrendLine = (
    current: { month: string; amount: number }[],
    lastYear: { month: string; amount: number }[],
    compareEnd: number
  ) => {
    // 1) Calculate differences (current - previous); null where there is nothing to compare yet
    const diff = current.map((c, i) => {
      if (i > compareEnd) return null;
      const currVal = c.amount;
      const prevVal = lastYear[i]?.amount || 0;
      return (Number.isFinite(currVal) && Number.isFinite(prevVal)) ? currVal - prevVal : 0;
    });

    // 2) Determine panel height (y-min/y-max)
    const allValues = [...current.map(d => d.amount), ...lastYear.map(d => d.amount)];
    const yMin = 0;
    const yMax = Math.max(...allValues, 1) * 1.15; // 15% margin

    // 3) Center point + scale (40% of panel height)
    const mid = (yMax + yMin) / 2;
    const maxAbsDiff = Math.max(...diff.map(d => Math.abs(d ?? 0)), 1);
    const scale = 0.4 * (yMax - yMin) / maxAbsDiff;

    // 4) Centered line in same scale as bars
    return { values: diff.map(d => (d === null ? null : mid + scale * d)), centerValue: mid };
  };

  const { current: monthlyInvoices, lastYear: lastYearInvoices, trend: trendData, compareEnd } = getInvoiceStats();
  const trendLine = trendData.values as (number | null)[];
  const centerValue = trendData.centerValue;
  const maxAmount = Math.max(
    ...monthlyInvoices.map(d => d.amount),
    ...lastYearInvoices.map(d => d.amount),
    ...trendLine.map(v => v ?? 0),
    1
  );

  // Same months this year and last year, e.g. "okt. 2025 – mars 2026: +3,2 %"
  const comparedCurrent = monthlyInvoices.slice(0, compareEnd + 1).reduce((s, d) => s + d.amount, 0);
  const comparedLastYear = lastYearInvoices.slice(0, compareEnd + 1).reduce((s, d) => s + d.amount, 0);
  const comparedChangePct = comparedLastYear > 0 ? ((comparedCurrent - comparedLastYear) / comparedLastYear) * 100 : null;

  // The settlement card uses the same figures as the next-settlement estimate: approved transactions dated up to
  // the next settlement (Settled Setup's last posted settlement + 1 month), and a separate line for those dated
  // after it (Settled.Portal 1.5.0.9+). Older BC versions only have the dashboard totals of all open items.
  const ns = overviewData?.nextSettlement;
  const settlement = ns && ns.laterOrders !== undefined
    ? {
        cutoff: ns.periodEnd as string,
        orders: ns.orders || 0,
        freight: ns.freightAmount || 0,
        commission: ns.commissionAmount || 0,
        commissionPct: ns.freightAmount ? (ns.commissionAmount / ns.freightAmount) * 100 : 0,
        fixedCosts: ns.fixedCosts || 0,
        fixed: ns.fixedCostAmount || 0,
        expenses: ns.expenses || 0,
        expenseAmount: ns.expenseAmount || 0,
        // BC's own total (includes third-party billing)
        net: ns.estimatedNetAmount || 0,
        laterOrders: ns.laterOrders || 0,
        laterExpenses: ns.laterExpenses || 0,
        laterNet:
          (ns.laterFreightAmount || 0) - (ns.laterCommissionAmount || 0) - (ns.laterExpenseAmount || 0) + (ns.laterThirdPartyBillingAmount || 0),
      }
    : {
        cutoff: null as string | null,
        orders: dashboardData?.settlementOrders || 0,
        freight: dashboardData?.settlementOrdersAmount || 0,
        commission: dashboardData?.commissionAmount || 0,
        commissionPct: dashboardData?.commissionPct || 0,
        fixedCosts: dashboardData?.fixedCosts || 0,
        fixed: dashboardData?.fixedCostAmount || 0,
        expenses: dashboardData?.expenses || 0,
        expenseAmount: dashboardData?.expensesAmount || 0,
        net:
          (dashboardData?.settlementOrdersAmount || 0) -
          (dashboardData?.commissionAmount || 0) -
          (dashboardData?.fixedCostAmount || 0) -
          (dashboardData?.expensesAmount || 0),
        laterOrders: 0,
        laterExpenses: 0,
        laterNet: 0,
      };

  // Published news from Business Central
  const newsItems = (newsData?.news || []).map((n: any) => ({
    id: n.systemId,
    headline: n.header,
    summary: String(n.body || '').length > 220 ? String(n.body).slice(0, 217).trimEnd() + '…' : String(n.body || ''),
    date: n.date,
  }));

  const latestNews = newsItems[0];
  const previousNews = newsItems.slice(1, 5);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar
        logoUrl={vendorData?.tenant?.logoUrl}
        companyName={vendorData?.tenant?.companyName}
        onLogout={handleLogout}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <TopBar
          userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username}
        />

        {/* Page Content */}
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          {/* Page Header */}
          <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem] mb-6">
            Översikt för {vendorData?.vendor?.displayName || vendorData?.vendor?.name || 'Leverantör'}
          </h1>

          <PayoutOverview overview={overviewData} />

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Card 1: Combined Financial Overview */}
            <div
              className="panel"
            >
              <div className="bg-surface p-6">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 ring-1 ring-inset ring-blue-100">
                    <Banknote className="h-5 w-5 text-blue-600" aria-hidden />
                  </span>
                  <div>
                    <h3 className="text-[15px] font-semibold text-ink">Avräkningsöversikt</h3>
                    {settlement.cutoff && (
                      <p className="text-xs text-gray-500">Till nästa avräkning {formatLongDate(settlement.cutoff)}</p>
                    )}
                  </div>
                </div>

              <div className="space-y-4">
                {/* Open Transactions */}
                <div
                  className="flex items-center justify-between py-2 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors duration-300 px-2 -mx-2"
                  onClick={() => navigate('/settlement-orders')}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-600">Transaktioner att avräkna</p>
                    <p className="text-xs text-gray-500">{settlement.orders} st</p>
                  </div>
                  <p className="text-lg font-semibold tabular-nums text-gray-900">{formatCurrency(settlement.freight)}</p>
                </div>

                {/* Commission (on each transaction line) */}
                <div
                  className="-mx-2 flex items-center justify-between border-b border-gray-100 px-2 py-2 transition-colors duration-300"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-600">Provision</p>
                    <p className="text-xs text-gray-500">{settlement.commissionPct ? `${settlement.commissionPct.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} % av transaktionerna` : 'På transaktionerna'}</p>
                  </div>
                  <p className="text-lg font-semibold tabular-nums text-gray-900">{formatDeduction(settlement.commission)}</p>
                </div>

                {/* Fixed Costs */}
                <div
                  className="-mx-2 flex items-center justify-between border-b border-gray-100 px-2 py-2 transition-colors duration-300 cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate('/fixed-costs')}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-600">Fasta kostnader</p>
                    <p className="text-xs text-gray-500">{settlement.fixedCosts} st</p>
                  </div>
                  <p className="text-lg font-semibold tabular-nums text-gray-900">{formatDeduction(settlement.fixed)}</p>
                </div>

                {/* Expenses (what the settlement run deducts) */}
                <div
                  className="-mx-2 flex items-center justify-between border-b border-gray-100 px-2 py-2 transition-colors duration-300 cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate('/open-expenses')}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-600">Utgifter och avdrag</p>
                    <p className="text-xs text-gray-500">{settlement.expenses} st</p>
                  </div>
                  <p className="text-lg font-semibold tabular-nums text-gray-900">{formatDeduction(settlement.expenseAmount)}</p>
                </div>

                {/* Total, after commission, fixed costs and expenses */}
                <div className="-mx-2 mt-2 flex items-center justify-between bg-navy px-5 py-4 text-white shadow-[0_16px_32px_-18px_rgb(0_22_49_/_0.9)]">
                  <div>
                    <p className="text-sm font-medium text-white/70">Netto att avräkna</p>
                    <p className="text-xs text-white/50">Transaktioner minus provision, fasta kostnader och utgifter</p>
                  </div>
                  <p className="text-2xl font-semibold tracking-tight tabular-nums text-[#5ac9e3]">
                    {formatCurrency(settlement.net)}
                  </p>
                </div>

                {/* Approved, but dated after the next settlement: they go on a later one */}
                {settlement.cutoff && (settlement.laterOrders > 0 || settlement.laterExpenses > 0) && (
                  <Link
                    to="/settlement-orders"
                    className="-mx-2 flex items-center justify-between px-2 pt-1 text-sm text-gray-600 transition-colors hover:text-ink"
                  >
                    <span>
                      Senare avräkning
                      <span className="block text-xs text-gray-500">
                        Daterade efter {formatDate(settlement.cutoff)} · {settlement.laterOrders} transaktioner
                        {settlement.laterExpenses > 0 && `, ${settlement.laterExpenses} ${settlement.laterExpenses === 1 ? 'utgift' : 'utgifter'}`}
                      </span>
                    </span>
                    <span className="font-medium tabular-nums text-gray-700">{formatCurrency(settlement.laterNet)}</span>
                  </Link>
                )}
              </div>
              </div>
            </div>

            {/* Right column: held transactions (compact) and news */}
            <div className="grid grid-rows-[auto_1fr] gap-6">
              <Link to="/settlement-orders?view=held" className="card card-hover block p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="flex items-center gap-2 text-[15px] font-semibold text-ink">
                    <Clock className="h-4 w-4 text-gray-500" aria-hidden />
                    Vilande transaktioner
                  </h3>
                  <ArrowRight className="h-4 w-4 text-gray-400" aria-hidden />
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500">Försenade · {dashboardData?.lateSettlementOrders || 0} st</dt>
                    <dd className="mt-0.5 text-lg font-semibold tabular-nums text-ink">{formatCurrency(dashboardData?.lateOrdersAmount)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Kreditrisk · {dashboardData?.creditRiskOrders || 0} st</dt>
                    <dd className="mt-0.5 text-lg font-semibold tabular-nums text-ink">{formatCurrency(dashboardData?.creditRiskOrdersAmount)}</dd>
                  </div>
                </dl>
              </Link>

              <NewsCard latestNews={latestNews} previousNews={previousNews} totalCount={newsItems.length} loading={newsLoading} />
            </div>
          </div>

          {/* Invoice Chart */}
          <div
            className="panel mb-8"
          >
            <div className="bg-surface p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-[15px] font-semibold text-ink">Bokförda fakturor (senaste 12 månaderna)</h2>
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gradient-to-t from-blue-600 to-blue-400 rounded"></div>
                    <span className="text-gray-600">Innevarande år</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-300 rounded"></div>
                    <span className="text-gray-600">Föregående år</span>
                  </div>
                  {compareEnd >= 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-1 bg-gradient-to-r from-red-500 to-green-500 rounded-full"></div>
                      <span className="text-gray-600">Skillnad mot föregående år</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                {comparedChangePct !== null ? (
                  <>
                    <p className={`flex items-center justify-end gap-1.5 text-lg font-semibold tabular-nums ${comparedChangePct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      <TrendingUp className={`h-4 w-4 ${comparedChangePct < 0 ? 'rotate-180' : ''}`} aria-hidden />
                      {comparedChangePct >= 0 ? '+' : '−'}
                      {Math.abs(comparedChangePct).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} %
                    </p>
                    <p className="text-xs text-gray-500">
                      {monthlyInvoices[0]?.month} – {monthlyInvoices[compareEnd]?.month} jämfört med samma månader året innan
                    </p>
                  </>
                ) : (
                  <p className="max-w-[28ch] text-xs text-gray-500">Ingen jämförelse än – det finns inga avslutade månader med avräkningar i år</p>
                )}
              </div>
            </div>
            <div className="relative" style={{ height: '320px' }}>
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-12 w-20 flex flex-col justify-between text-xs text-gray-500">
                <div className="text-right pr-2">
                  {maxAmount.toLocaleString('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="text-right pr-2">
                  {(maxAmount * 0.75).toLocaleString('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="text-right pr-2">
                  {(maxAmount * 0.5).toLocaleString('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="text-right pr-2">
                  {(maxAmount * 0.25).toLocaleString('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="text-right pr-2">0 kr</div>
              </div>

              {/* Chart area */}
              <div className="absolute left-20 right-0 top-0 bottom-0">
                {/* Center line (50% mark) - behind bars */}
                <div className="absolute left-0 right-0 border-t border-gray-300 border-dashed" style={{ top: '50%', bottom: 'auto' }}></div>

                <div className="flex items-end justify-between gap-1 h-full pb-12">
                  {monthlyInvoices.map((data, index) => {
                    const currentHeightPercent = maxAmount > 0 ? (data.amount / maxAmount) * 100 : 0;
                    const lastYearHeightPercent = maxAmount > 0 ? (lastYearInvoices[index]?.amount / maxAmount) * 100 : 0;
                    const compared = index <= compareEnd;
                    const inProgress = index === monthlyInvoices.length - 1;
                    return (
                      <div key={index} className={`flex-1 flex flex-row items-end h-full justify-center gap-0.5 group relative ${compared ? '' : 'opacity-45'}`}>
                        {/* Last Year Bar (Grey) */}
                        <div
                          className="bg-gray-300 w-5/12 rounded-t-sm transition-all duration-500 hover:bg-gray-400 cursor-pointer"
                          style={{ height: `${lastYearHeightPercent}%` }}
                          title={`${lastYearInvoices[index]?.month}: ${lastYearInvoices[index]?.amount.toLocaleString('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}`}
                        />
                        {/* Current Year Bar (Blue) */}
                        <div
                          className="bg-gradient-to-t from-blue-600 to-blue-400 w-5/12 rounded-t-sm transition-all duration-500 hover:from-blue-700 hover:to-blue-500 cursor-pointer"
                          style={{ height: `${currentHeightPercent}%` }}
                          title={`${data.month}: ${data.amount.toLocaleString('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}`}
                        />
                        {/* Tooltip on hover */}
                        <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap z-10">
                          <div className="font-semibold mb-1">{data.month}</div>
                          <div className="text-blue-300">
                            Detta år: {data.amount.toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </div>
                          <div className="text-gray-300">
                            Förra året: {lastYearInvoices[index]?.amount.toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </div>
                          {!compared ? (
                            <div className="mt-1 text-gray-400">
                              {inProgress ? 'Pågående månad – jämförs när den är avslutad' : 'Inga avräkningar ännu – jämförs inte'}
                            </div>
                          ) : lastYearInvoices[index]?.amount > 0 && (
                            <div className={`mt-1 font-medium ${data.amount >= lastYearInvoices[index]?.amount ? 'text-green-400' : 'text-red-400'}`}>
                              {((data.amount - lastYearInvoices[index]?.amount) / lastYearInvoices[index]?.amount * 100).toFixed(1)}%
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* X-axis labels */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-between">
                  {monthlyInvoices.map((data, index) => (
                    <div key={index} className="flex-1 text-center">
                      <div className="text-[10px] text-gray-600 font-medium transform -rotate-45 origin-center whitespace-nowrap mt-1">
                        {data.month}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Trend line with dynamic color (rendered on top) */}
                <svg className="absolute inset-0 w-full pointer-events-none" style={{ height: 'calc(100% - 48px)' }} viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    {/* Create gradients for each line segment */}
                    {trendLine.map((value, index) => {
                      const prevValue = trendLine[index - 1];
                      if (index === 0 || value === null || prevValue === null || prevValue === undefined) return null;
                      // Determine color based on direction (green if above center value, red if below)
                      const startColor = prevValue > centerValue ? '#22c55e' : '#ef4444';
                      const endColor = value > centerValue ? '#22c55e' : '#ef4444';
                      return (
                        <linearGradient key={`gradient-${index}`} id={`line-gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor={startColor} />
                          <stop offset="100%" stopColor={endColor} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  {/* Draw line segments with individual gradients */}
                  {trendLine.map((value, index) => {
                    const prevValue = trendLine[index - 1];
                    // Only between two compared months; points sit over the centre of each month's bars
                    if (index === 0 || value === null || prevValue === null || prevValue === undefined) return null;
                    const x1 = ((index - 0.5) / trendLine.length) * 100;
                    const y1 = 100 - (maxAmount > 0 ? (prevValue / maxAmount) * 100 : 0);
                    const x2 = ((index + 0.5) / trendLine.length) * 100;
                    const y2 = 100 - (maxAmount > 0 ? (value / maxAmount) * 100 : 0);
                    return (
                      <line
                        key={`line-${index}`}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={`url(#line-gradient-${index})`}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })}
                </svg>
              </div>
            </div>
            </div>
          </div>

          {/* Top 5 Customers and Resources */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top 5 Customers */}
            <div
              className="panel"
            >
              <div className="bg-surface p-6 h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <h2 className="text-[15px] font-semibold text-ink">Topp 5 kunder</h2>
                  </div>
                </div>
                <div className="space-y-3">
                {(() => {
                  // Group customers by customerNo and sum their settlement amounts
                  const customerMap = new Map();
                  (customersData?.customers || []).forEach((customer: any) => {
                    const key = customer.customerNo;
                    if (customerMap.has(key)) {
                      const existing = customerMap.get(key);
                      existing.settlementOrderAmount += (customer.settlementOrderAmount || 0);
                    } else {
                      customerMap.set(key, {
                        customerNo: customer.customerNo,
                        customerName: customer.customerName || customer.name,
                        settlementOrderAmount: customer.settlementOrderAmount || 0
                      });
                    }
                  });

                  return Array.from(customerMap.values())
                    .sort((a: any, b: any) => b.settlementOrderAmount - a.settlementOrderAmount)
                    .slice(0, 5)
                    .map((customer: any, index: number) => (
                    <div
                      key={customer.customerNo || index}
                      className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/customers/${encodeURIComponent(customer.customerNo)}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full text-blue-600 font-semibold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{customer.customerName || customer.name}</p>
                          <p className="text-xs text-gray-500">{customer.customerNo}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {(customer.settlementOrderAmount || 0).toLocaleString('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </p>
                        <p className="text-xs text-gray-500">Avräkning</p>
                      </div>
                    </div>
                  ));
                })()}
                {(!customersData?.customers || customersData.customers.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Inga kunder hittades</p>
                  </div>
                )}
              </div>
              </div>
            </div>

            {/* Top 5 Resources */}
            <div
              className="panel"
            >
              <div className="bg-surface p-6 h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-purple-600" />
                    <h2 className="text-[15px] font-semibold text-ink">Topp 5 resurser</h2>
                  </div>
                </div>
                <div className="space-y-3">
                {(resourcesData?.resources || [])
                  .map((resource: any) => {
                    // Parse settlement amounts and calculate last 12 months total
                    let totalAmount = 0;
                    if (resource.settlementOrderAmounts) {
                      try {
                        const amounts = JSON.parse(resource.settlementOrderAmounts);
                        // Get current date and 12 months ago
                        const now = new Date();
                        const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 12, 1);

                        // Filter and sum only last 12 months
                        totalAmount = amounts
                          .filter((item: any) => {
                            if (!item.month) return false;
                            const itemDate = new Date(item.month + '-01');
                            return itemDate >= twelveMonthsAgo;
                          })
                          .reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
                      } catch (e) {
                        // Handle parse error
                      }
                    }
                    return { ...resource, totalAmount };
                  })
                  .sort((a: any, b: any) => b.totalAmount - a.totalAmount)
                  .slice(0, 5)
                  .map((resource: any, index: number) => {
                    const totalAmount = resource.totalAmount;

                    return (
                      <div
                        key={resource.no || index}
                        className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/resources/${resource.systemId}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full text-purple-600 font-semibold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{resource.name}</p>
                            <p className="text-xs text-gray-500">{resource.registrationNo || resource.no}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {totalAmount.toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </p>
                          <p className="text-xs text-gray-500">12 månader</p>
                        </div>
                      </div>
                    );
                  })}
                {(!resourcesData?.resources || resourcesData.resources.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <Truck className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Inga resurser hittades</p>
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
